import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
  SparklesIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { authApi, getErrorMessage } from '../services/api';

// 第三方扫码平台（跟 docxcheckerexe 暗标一致：前端直接调 zbapi）
const ZBAPI_POLL_URL = 'https://zbapi.ksmso.cn/index/index/ticket';

interface VisualLoginProps {
  onLogin: (username: string, password: string) => Promise<void>;
  /** zbapi JWT 模式：扫码后 zbapi 返回 HS256 JWT，用它换签我们的 JWT */
  onWechatLoginByJwt?: (zbapi_jwt: string) => Promise<void>;
  wechatLoginEnabled?: boolean;
}

const WECHAT_POLL_INTERVAL_MS = 4000;
const WECHAT_POLL_MAX_ATTEMPTS = 12;

const VisualLogin: React.FC<VisualLoginProps> = ({
  onLogin,
  onWechatLoginByJwt,
  wechatLoginEnabled = false,
}) => {
  const [mode, setMode] = useState<'account' | 'wechat'>('account');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // 微信扫码二维码相关状态
  const [qrcodeUrl, setQrcodeUrl] = useState('');
  const [ticket, setTicket] = useState('');
  const [qrcodeLoading, setQrcodeLoading] = useState(false);
  const [qrcodeExpired, setQrcodeExpired] = useState(false);
  const pollAttemptsRef = useRef(0);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wechatLoginSubmittedRef = useRef(false);  // 防止 StrictMode 或定时器残留导致重复提交
  const pollingTicketRef = useRef('');  // 保证同一个 ticket 只创建一个定时器

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      console.log('[VisualLogin] clearPollTimer() 清掉旧定时器, ticket=%s', pollingTicketRef.current);
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => clearPollTimer();
  }, [clearPollTimer]);

  /**
   * 获取微信扫码二维码。
   * 调用后端 /api/auth/wechat/qrcode 代理第三方平台获取二维码和 ticket。
   */
  const fetchQrcode = useCallback(async () => {
    if (!wechatLoginEnabled) {
      return;
    }
    setQrcodeLoading(true);
    setError('');
    setQrcodeExpired(false);
    setQrcodeUrl('');
    setTicket('');
    pollAttemptsRef.current = 0;
    clearPollTimer();

    try {
      const response = await authApi.getWechatQrcode();
      const { code_url, ticket: ticketValue } = response.data.data;
      setQrcodeUrl(code_url);
      setTicket(ticketValue);
    } catch (requestError) {
      setError(getErrorMessage(requestError, '获取微信二维码失败'));
    } finally {
      setQrcodeLoading(false);
    }
  }, [wechatLoginEnabled, clearPollTimer]);

  /**
   * 切换到微信登录标签时自动获取二维码。
   */
  useEffect(() => {
    if (mode === 'wechat' && wechatLoginEnabled && !qrcodeUrl && !qrcodeLoading) {
      fetchQrcode();
    }
    if (mode !== 'wechat') {
      clearPollTimer();
    }
  }, [mode, wechatLoginEnabled, qrcodeUrl, qrcodeLoading, fetchQrcode, clearPollTimer]);

  /**
   * ticket 变化后开始轮询扫码状态。
   *
   * ⚠️ zbapi 平台已升级（2026-09-01）：扫码后直接返回 HS256 JWT（payload 含
   * sub=member_id），不再返回 RSA 密文。前端拿到 JWT 后调后端 /wechat/login-jwt
   * 换签我们自己的 JWT。
   *
   * zbapi poll 返回值变化规律（同一个 ticket）：
   *   未扫码: { data: { code: 400 } }  (无 user 对象)
   *   扫码后: { data: { user: { code: 200, access_token: "HS256 JWT" } } }
   */
  useEffect(() => {
    // 无论什么情况都返回 cleanup，防止 StrictMode 或依赖变化时定时器泄漏
    if (!ticket || !onWechatLoginByJwt) {
      console.log('[VisualLogin] poll useEffect: 缺少 ticket 或 callback, skip');
      return () => clearPollTimer();
    }

    // 同一个 ticket 不重复创建定时器（防止 StrictMode/依赖抖动创建多个）
    if (pollingTicketRef.current === ticket) {
      console.warn('[VisualLogin] poll useEffect: 同一个 ticket=%s 已在轮询中, 跳过', ticket);
      return () => clearPollTimer();
    }
    pollingTicketRef.current = ticket;

    console.log('[VisualLogin] poll useEffect: 创建新定时器, ticket=%s', ticket);
    clearPollTimer();
    pollAttemptsRef.current = 0;
    wechatLoginSubmittedRef.current = false;  // 新 ticket 重置提交标志

    pollTimerRef.current = setInterval(async () => {
      if (wechatLoginSubmittedRef.current) {
        return;  // 已经提交过登录了，不再处理后续 poll 结果
      }
      if (pollAttemptsRef.current >= WECHAT_POLL_MAX_ATTEMPTS) {
        console.log('[VisualLogin] poll 超过最大次数 %d, 停止', WECHAT_POLL_MAX_ATTEMPTS);
        clearPollTimer();
        setQrcodeExpired(true);
        pollingTicketRef.current = '';
        return;
      }
      pollAttemptsRef.current += 1;

      try {
        // 直接调 zbapi poll（跟 docxcheckerexe 暗标一致，不走我们后端代理）
        const zbRes = await axios.post(ZBAPI_POLL_URL, { ticket });
        const zbData = zbRes.data;  // { code, msg, data: { code, user?: { access_token, code, msg } } }
        const user = zbData?.data?.user;
        const userCode = user?.code;

        console.log(
          '[VisualLogin] poll #%d: ticket=%s, user.code=%s, access_token前30=%s',
          pollAttemptsRef.current,
          ticket.slice(0, 8),
          userCode,
          user?.access_token?.slice(0, 30),
        );

        if (userCode === 200 && user.access_token && !wechatLoginSubmittedRef.current) {
          const zbapiJwt = user.access_token;

          // zbapi 平台已升级，返回的是 HS256 JWT（eyJ 开头）
          // 现在正常接收，直接传给后端换签我们自己的 JWT
          if (!zbapiJwt.startsWith('eyJ')) {
            console.warn(
              '[VisualLogin] ⚠️ zbapi poll 返回非 JWT 格式！前30字符=%s',
              zbapiJwt.slice(0, 30),
            );
          }

          // 扫码确认成功！调后端 /wechat/login-jwt 换签
          console.log('[VisualLogin] ✅ 拿到 zbapi JWT! 长度=%d, 前40=%s', zbapiJwt.length, zbapiJwt.slice(0, 40));
          wechatLoginSubmittedRef.current = true;  // 立即锁死，防止并发
          clearPollTimer();
          pollingTicketRef.current = '';
          setIsSubmitting(true);
          setError('');
          onWechatLoginByJwt(zbapiJwt)
            .then(() => {})
            .catch((requestError) => {
              setError(getErrorMessage(requestError, '微信扫码登录失败，请重试'));
              setQrcodeExpired(true);
              wechatLoginSubmittedRef.current = false;  // 失败后允许重新提交
            })
            .finally(() => setIsSubmitting(false));
        } else if (userCode === 400) {
          // zbapi 返回业务错误（如 ticket 已取消/过期），停止轮询
          console.log('[VisualLogin] zbapi 返回 400, ticket=%s 停止轮询', ticket.slice(0, 8));
          clearPollTimer();
          setQrcodeExpired(true);
          pollingTicketRef.current = '';
        }
        // userCode 不存在 → 未扫码，继续下次轮询
      } catch (err) {
        // 轮询单次失败不中断，继续下次轮询
        console.warn('[VisualLogin] poll 异常: %s', (err as Error).message);
      }
    }, WECHAT_POLL_INTERVAL_MS);

    return () => clearPollTimer();
  }, [ticket, onWechatLoginByJwt, clearPollTimer]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await onLogin(username.trim(), password);
    } catch (requestError) {
      setError(getErrorMessage(requestError, '登录失败，请检查账号和密码'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefreshQrcode = () => {
    fetchQrcode();
  };

  return (
    <main className="min-h-screen bg-[#f4f7fb] p-5 lg:p-8">
      <div className="mx-auto grid min-h-[calc(100vh-40px)] max-w-[1480px] overflow-hidden rounded-[28px] bg-white shadow-[0_28px_90px_rgba(31,52,86,0.12)] lg:grid-cols-[1.08fr_0.92fr]">
        <section className="relative hidden overflow-hidden bg-gradient-to-br from-[#eaf3ff] via-[#f3f8ff] to-[#e4f0ff] p-14 lg:flex lg:flex-col">
          <div className="absolute -left-28 top-24 h-80 w-80 rounded-full bg-blue-300/20 blur-2xl" />
          <div className="absolute -bottom-24 right-12 h-96 w-96 rounded-full bg-indigo-300/20 blur-3xl" />

          <div className="relative z-10 flex items-center gap-3 text-[var(--bid-color-primary)]">
            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-[var(--bid-color-primary)] text-white shadow-lg shadow-violet-200">
              <SparklesIcon className="h-6 w-6" />
            </div>
            <div>
              <div className="text-xl font-semibold tracking-wide">易成标</div>
              <div className="text-xs text-slate-500">AI 智能标书编制平台</div>
            </div>
          </div>

          <div className="relative z-10 my-auto max-w-xl">
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white/75 px-4 py-2 text-sm text-blue-700 backdrop-blur">
              <SparklesIcon className="h-4 w-4" />
              AI 驱动的专业标书写作
            </div>
            <h1 className="text-5xl font-semibold leading-[1.22] tracking-tight text-[#18233e]">
              让每一份标书
              <br />
              更专业、更高效
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-8 text-slate-600">
              从招标文件解析到目录规划、正文生成与文档导出，一站式完成标书编制。
            </p>

            <div className="relative mt-12 h-72">
              <div className="absolute left-8 top-0 w-[390px] rotate-[-2deg] rounded-3xl border border-white/80 bg-white/90 p-6 shadow-[0_24px_60px_rgba(70,100,160,0.18)] backdrop-blur">
                <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-blue-50 text-blue-600">
                      <DocumentTextIcon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="h-2.5 w-36 rounded-full bg-slate-200" />
                      <div className="mt-2 h-2 w-24 rounded-full bg-slate-100" />
                    </div>
                  </div>
                  <CheckCircleIcon className="h-6 w-6 text-emerald-500" />
                </div>
                {[88, 68, 78, 52].map((width, index) => (
                  <div key={width} className="mt-5 flex items-center gap-3">
                    <span className="grid h-6 w-6 place-items-center rounded-lg bg-blue-50 text-xs text-blue-600">
                      {index + 1}
                    </span>
                    <div className="h-2 rounded-full bg-slate-100" style={{ width: `${width}%` }} />
                  </div>
                ))}
              </div>
              <div className="absolute bottom-3 right-6 w-52 rounded-2xl bg-[var(--bid-color-primary)] p-5 text-white shadow-xl shadow-violet-300/40">
                <div className="text-sm text-blue-100">标书生成进度</div>
                <div className="mt-2 text-3xl font-semibold">智能协作</div>
                <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/20">
                  <div className="h-full w-3/4 rounded-full bg-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="relative z-10 text-sm text-slate-500">专业 · 安全 · 高效</div>
        </section>

        <section className="flex items-center justify-center px-6 py-12 sm:px-12 lg:px-20">
          <div className="w-full max-w-[460px]">
            <div className="mb-10 lg:hidden">
              <div className="flex items-center gap-3 text-[var(--bid-color-primary)]">
                <SparklesIcon className="h-8 w-8" />
                <span className="text-xl font-semibold">易成标 AI 标书</span>
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-[var(--bid-color-primary)]">欢迎回来</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900">
                欢迎使用易成标标书 AI
              </h2>
              <p className="mt-3 text-slate-500">登录后继续创建和管理您的标书项目</p>
            </div>

            <div className="mt-9 grid grid-cols-2 rounded-xl bg-slate-100 p-1">
              <button
                type="button"
                onClick={() => setMode('account')}
                className={`rounded-lg py-2.5 text-sm font-medium transition ${
                  mode === 'account'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                账号密码登录
              </button>
              <button
                type="button"
                onClick={() => setMode('wechat')}
                className={`rounded-lg py-2.5 text-sm font-medium transition ${
                  mode === 'wechat'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                微信扫码登录
              </button>
            </div>

            {mode === 'account' ? (
              <form className="mt-8 space-y-5" onSubmit={submit}>
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-slate-700">账号</span>
                  <div className="relative">
                    <UserIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                      value={username}
                      onChange={(event) => setUsername(event.target.value)}
                      autoComplete="username"
                      placeholder="请输入账号"
                      required
                      className="h-12 w-full rounded-xl border-slate-200 pl-12 pr-4 text-sm focus:border-[var(--bid-color-primary)] focus:ring-[var(--bid-color-primary)]"
                    />
                  </div>
                </label>

                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-slate-700">密码</span>
                  <div className="relative">
                    <LockClosedIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="请输入密码"
                      required
                      className="h-12 w-full rounded-xl border-slate-200 pl-12 pr-12 text-sm focus:border-[var(--bid-color-primary)] focus:ring-[var(--bid-color-primary)]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((value) => !value)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      aria-label={showPassword ? '隐藏密码' : '显示密码'}
                    >
                      {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                </label>

                {error && (
                  <div role="alert" className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || !username.trim() || !password}
                  className="group flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[var(--bid-color-primary)] text-sm font-medium text-white shadow-lg shadow-violet-200 transition hover:bg-[var(--bid-color-primary-hover)] disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
                >
                  {isSubmitting ? '登录中...' : '登录'}
                  {!isSubmitting && <ArrowRightIcon className="h-4 w-4 transition group-hover:translate-x-1" />}
                </button>

                <p className="text-center text-sm text-slate-500">
                  还没有账号？
                  <span className="ml-1 text-[var(--bid-color-primary)]">请联系管理员开通</span>
                </p>
              </form>
            ) : (
              <div className="mt-8 text-center">
                {error && (
                  <div role="alert" className="mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
                    {error}
                  </div>
                )}

                {wechatLoginEnabled ? (
                  <div className="space-y-6">
                    <div className="flex flex-col items-center">
                      <p className="font-medium text-slate-800">微信扫码登录</p>
                      <p className="mt-1 text-sm text-slate-500">请使用微信扫描下方二维码</p>

                      <div className="relative mt-6 h-[180px] w-[180px]">
                        {qrcodeLoading ? (
                          <div className="grid h-full w-full place-items-center rounded-lg border border-slate-200 bg-slate-50">
                            <SparklesIcon className="h-8 w-8 animate-pulse text-[var(--bid-color-primary)]" />
                          </div>
                        ) : qrcodeUrl ? (
                          <>
                            <img
                              src={qrcodeUrl}
                              alt="微信扫码登录二维码"
                              className="h-full w-full rounded-lg border border-slate-200 object-contain"
                            />
                            {qrcodeExpired && (
                              <div className="absolute inset-0 grid place-items-center rounded-lg bg-black/60">
                                <div className="flex flex-col items-center">
                                  <button
                                    type="button"
                                    onClick={handleRefreshQrcode}
                                    className="grid h-10 w-10 place-items-center rounded-full bg-white text-[var(--bid-color-primary)]"
                                    aria-label="刷新二维码"
                                  >
                                    <ArrowPathIcon className="h-5 w-5" />
                                  </button>
                                  <span className="mt-2 text-sm text-white">点击刷新</span>
                                </div>
                              </div>
                            )}
                            {isSubmitting && !qrcodeExpired && (
                              <div className="absolute inset-0 grid place-items-center rounded-lg bg-white/80">
                                <span className="text-sm text-slate-600">正在登录...</span>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="grid h-full w-full place-items-center rounded-lg border border-slate-200 bg-slate-50">
                            <button
                              type="button"
                              onClick={handleRefreshQrcode}
                              className="flex flex-col items-center text-slate-500"
                            >
                              <ArrowPathIcon className="h-8 w-8" />
                              <span className="mt-2 text-sm">点击重新获取</span>
                            </button>
                          </div>
                        )}
                      </div>

                      {!qrcodeExpired && qrcodeUrl && !isSubmitting && (
                        <p className="mt-4 text-sm text-slate-400">
                          二维码有效期内请尽快扫码
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div
                    role="status"
                    className="mx-auto grid min-h-52 max-w-sm place-items-center rounded-3xl border border-amber-200 bg-amber-50 px-8 py-10"
                  >
                    <div>
                      <LockClosedIcon className="mx-auto h-16 w-16 text-amber-500" />
                      <p className="mt-5 font-medium text-slate-800">微信扫码登录当前不可用</p>
                      <p className="mt-2 text-sm leading-6 text-slate-600">
                        微信扫码登录服务未启用，请使用账号密码登录，或联系管理员开通。
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
};

export default VisualLogin;
